import React, { useState } from "react";
import {
  Form,
  Input,
  Button,
  Upload,
  Typography,
  message as antdMessage,
  Row,
  Col,
  Card,
  Checkbox,
  Select,
  Collapse,
  Spin,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import SummaryApi from "../../../common/index";
import "../../../Styles/SellerStylings/SellerSignup.css";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;
const { Panel } = Collapse;

function SellerSignup() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [formMessage, setFormMessage] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [areas, setAreas] = useState([]);
  const [loadingPincode, setLoadingPincode] = useState(false);

  // separate loading states
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [registering, setRegistering] = useState(false);

  // =================== FILE UPLOAD ===================
  const handleFileChange = ({ file, fileList }) => {
    if (file && file.size > 5000 * 1024) {
      antdMessage.error("❌ File size must be less than 5MB");
      return;
    }
    setFileList(fileList);
  };

  // =================== PINCODE LOOKUP ===================
  const handlePincodeChange = async (e) => {
    const pincode = e.target.value.trim();
    form.setFieldsValue({ storePincode: pincode });

    if (pincode.length === 6) {
      try {
        setLoadingPincode(true);
        const response = await axios({
          method: SummaryApi.fetchPincodeDetails(pincode).method,
          url: SummaryApi.fetchPincodeDetails(pincode).url,
        });

        if (response.data && response.data.success) {
          const { district, state, areas: fetchedAreas } = response.data;
          form.setFieldsValue({
            district: district || "",
            state: state || "",
          });
          setAreas(Array.isArray(fetchedAreas) ? fetchedAreas : []);
        } else {
          antdMessage.error(response.data?.message || "Pincode not found");
          setAreas([]);
        }
      } catch (error) {
        console.error("Pincode fetch error:", error);
        antdMessage.error("Error fetching address details");
        setAreas([]);
      } finally {
        setLoadingPincode(false);
      }
    } else {
      setAreas([]);
      form.setFieldsValue({ district: "", state: "" });
    }
  };

  // =================== SEND OTP ===================
  const handleSendOtp = async () => {
    const phone = form.getFieldValue("phone");
    if (!phone) return antdMessage.warning("Please enter phone number first");

    try {
      setSendingOtp(true);
      const res = await axios.post(SummaryApi.sendOtpSeller.url, { phone });

      if (res.data?.success) {
        antdMessage.success("OTP sent successfully!");
        setOtpSent(true);
        startCooldown();
      } else {
        antdMessage.error(res.data?.message || "Failed to send OTP");
      }
    } catch (err) {
      console.error(err);
      antdMessage.error(err.response?.data?.message || "Error sending OTP");
    } finally {
      setSendingOtp(false);
    }
  };

  // =================== VERIFY OTP ===================
  const handleVerifyOtp = async () => {
    const phone = form.getFieldValue("phone");
    const otp = form.getFieldValue("otp");
    if (!otp) return antdMessage.warning("Enter OTP");

    try {
      setVerifyingOtp(true);
      const res = await axios.post(SummaryApi.verifyOtpSeller.url, { phone, otp });

      if (res.data?.success) {
        antdMessage.success("Mobile number verified successfully!");
        setOtpVerified(true);
      } else {
        antdMessage.error(res.data?.message || "OTP verification failed");
      }
    } catch (err) {
      console.error(err);
      antdMessage.error(err.response?.data?.message || "Verification failed");
    } finally {
      setVerifyingOtp(false);
    }
  };

  // =================== SIGNUP SUBMIT ===================
  const onFinish = async (values) => {
    if (!agreed) {
      antdMessage.warning("You must agree to the terms and conditions.");
      return;
    }
    if (!otpVerified) {
      return antdMessage.warning("Please verify your mobile number first");
    }

    try {
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (values[key] !== undefined && values[key] !== null) {
          formData.append(key, values[key]);
        }
      });
      if (fileList && fileList[0] && fileList[0].originFileObj) {
        formData.append("identityProofs", fileList[0].originFileObj);
      }

      setRegistering(true);
      const res = await axios.post(SummaryApi.sellerSignup.url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.success || res.status === 200) {
        setFormMessage("✅ Account created successfully!");
        antdMessage.success("Seller registered successfully!");
        navigate("/seller-signin");
      } else {
        setFormMessage(res.data?.message || "❌ Signup failed");
        antdMessage.error(res.data?.message || "Signup failed");
      }
    } catch (err) {
      console.error("Signup error:", err);
      setFormMessage(err.response?.data?.message || "❌ Signup failed");
      antdMessage.error(err.response?.data?.message || "Signup failed");
    } finally {
      setRegistering(false);
    }
  };

  // =================== RESEND OTP TIMER ===================
  const startCooldown = () => {
    let time = 30;
    setResendCooldown(time);
    const interval = setInterval(() => {
      time -= 1;
      setResendCooldown(time);
      if (time <= 0) clearInterval(interval);
    }, 1000);
  };

  return (
    <div className="seller-signup-wrapper">
      <Card className="seller-signup-card" bordered={false}>
        <Title level={2} className="seller-signup-title">
          Register Your Store With Booklocal
        </Title>
        <Text type="secondary" className="seller-signup-subtitle">
          List your shop on BookLocal and start selling to thousands of customers
        </Text>

        <Form form={form} layout="vertical" onFinish={onFinish}>
          {/* 1️⃣ ACCOUNT & MOBILE VERIFICATION */}
         <Title level={4} style={{ marginTop: "10px" }}>
  🧾 Account & Mobile Verification
</Title>

{/* Store Name - Full Width Row */}
<Row gutter={24}>
  <Col xs={24}>
    <Form.Item
      name="storeName"
      label="Store Name"
      rules={[{ required: true, message: "Please enter your store name" }]}
      style={{ width: "100%" }}
    >
      <Input placeholder="Enter your store name" />
    </Form.Item>
  </Col>
</Row>

{/* Two Column Layout Below */}
<Row gutter={24}>
  <Col xs={24} md={12}>
    <Form.Item
      name="name"
      label="Full Name"
      rules={[{ required: true, message: "Please enter your name" }]}
    >
      <Input placeholder="Enter your full name" />
    </Form.Item>

    <Form.Item
      name="phone"
      label="Phone Number"
      rules={[{ required: true, message: "Enter valid phone number" }]}
    >
      <Row gutter={8}>
        <Col span={16}>
          <Input placeholder="Enter phone number" 
            maxLength={10}
          disabled={otpVerified} />
        </Col>
        <Col span={8}>
          {!otpSent ? (
            <Button
            style={{background:"#1129ce",color:"white"}}
              block
              onClick={handleSendOtp}
              loading={sendingOtp}
            >
              Send OTP
            </Button>
          ) : (
            <Button
              type="primary"
              block
              onClick={handleVerifyOtp}
              loading={verifyingOtp}
              disabled={otpVerified}
            >
              {otpVerified ? "Verified" : "Verify OTP"}
            </Button>
          )}
        </Col>
      </Row>
    </Form.Item>

    {otpSent && !otpVerified && (
      <>
        <Form.Item
          name="otp"
          label="Enter OTP"
          rules={[{ required: true, message: "Enter OTP" }]}
        >
          <Input placeholder="Enter OTP" maxLength={6} />
        </Form.Item>

        <Button
          type="link"
          disabled={resendCooldown > 0}
          onClick={handleSendOtp}
          style={{ paddingLeft: 0 }}
        >
          {resendCooldown > 0
            ? `Resend OTP in ${resendCooldown}s`
            : "Resend OTP"}
        </Button>
      </>
    )}
  </Col>

  <Col xs={24} md={12}>
    <Form.Item
      name="email"
      label="Email"
      rules={[{ required: true, type: "email" }]}
    >
      <Input placeholder="Enter email address" />
    </Form.Item>

    <Form.Item
      name="password"
      label="Password"
      rules={[{ required: true, message: "Enter password" }]}
    >
      <Input.Password placeholder="Create a password" />
    </Form.Item>
  </Col>
</Row>


          {/* 2️⃣ STORE DETAILS */}
        <Collapse defaultActiveKey={[]} bordered={false} style={{ marginTop: "20px" }}>
  <Panel header="📍 Store & Address Details" key="1">

    {/* 🏠 Address Line 1 - Full Width */}
    <Row gutter={24}>
      <Col span={24}>
        <Form.Item
          name="storeStreet"
          label="Address Line 1"
          rules={[{ required: true, message: "Enter street or landmark" }]}
        >
          <Input placeholder="Enter street / landmark" />
        </Form.Item>
      </Col>
    </Row>

    {/* 📮 Pincode & City side by side */}
    <Row gutter={24}>
      <Col xs={24} md={12}>
        <Form.Item
          name="storePincode"
          label="Pincode"
          rules={[{ required: true, message: "Enter pincode" }]}
        >
          <Input
            placeholder="Enter 6-digit pincode"
            maxLength={6}
            onChange={handlePincodeChange}
          />
        </Form.Item>
      </Col>

      <Col xs={24} md={12}>
        <Form.Item
          name="storeCity"
          label="City"
          rules={[{ required: true, message: "Enter city" }]}
        >
          <Input placeholder="Enter city" />
        </Form.Item>
      </Col>
    </Row>

    {/* 🏘️ Area / Locality & District below */}
    <Row gutter={24}>
      <Col xs={24} md={12}>
        {areas.length > 0 ? (
          <Form.Item
            name="storeArea"
            label="Area / Locality"
            rules={[{ required: true, message: "Select area" }]}
          >
            <Select placeholder="Select area">
              {areas.map((area, idx) => (
                <Select.Option key={idx} value={area}>
                  {area}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        ) : (
          <Form.Item
            name="storeArea"
            label="Area / Locality"
            rules={[{ required: true, message: "Enter area" }]}
          >
            <Input placeholder="Enter area / locality" />
          </Form.Item>
        )}
      </Col>

      <Col xs={24} md={12}>
        <Form.Item name="district" label="District">
          <Input placeholder="Auto-filled district (editable)" />
        </Form.Item>
      </Col>
    </Row>

    {/* 🗺️ State full width below */}
    <Row gutter={24}>
      <Col span={24}>
        <Form.Item name="state" label="State">
          <Input placeholder="Auto-filled state (editable)" />
        </Form.Item>
      </Col>
    </Row>
  </Panel>
</Collapse>


          {/* 3️⃣ LICENSE & PROOF */}
          <Title level={4} style={{ marginTop: "25px" }}>
            📄 License & Proof
          </Title>
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                name="gstNo"
                label="GST Number"
                rules={[{ required: true, message: "Enter GST number" }]}
              >
                <Input placeholder="Enter GST number" />
              </Form.Item>
              <Form.Item
                name="registrationNo"
                label="License Registration No."
                rules={[{ required: true, message: "Enter registration number" }]}
              >
                <Input placeholder="Enter registration number" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item label="Identity / License Proof">
                <Upload
                  beforeUpload={() => false}
                  onChange={handleFileChange}
                  fileList={fileList}
                  accept=".jpg,.jpeg,.png,.pdf"
                >
                  <Button icon={<UploadOutlined />}>Upload Proof</Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Checkbox
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            >
              By continuing, you agree to Booklocal's
              <a href="https://www.booklocal.co.in/privacy-policy" target="_blank"> Terms of Use</a>{" "}
              and acknowledge our
              <a href="https://www.booklocal.co.in/terms-of-use" target="_blank"> Privacy Policy</a>
            </Checkbox>
          </Form.Item>

          <Button
      
            htmlType="submit"
            block
            size="large"
            style={{ backgroundColor: "#1129ce", color: "white", fontWeight: "bold" }}
            loading={registering}
          >
            Create Seller Account
          </Button>

          {formMessage && <p className="seller-form-message">{formMessage}</p>}
        </Form>

        <div style={{ textAlign: "center", marginTop: "18px" }}>
          <Text>
            Already registered?{" "}
            <a href="/seller-signin" style={{ color: "#1677ff" }}>
              Sign in here
            </a>
          </Text>
        </div>

        {loadingPincode && (
          <div style={{ textAlign: "center", marginTop: 10 }}>
            <Spin tip="Fetching location details..." />
          </div>
        )}
      </Card>
    </div>
  );
}

export default SellerSignup;
